import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, message, Spin, Input, Button } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../api/api";
import { Link } from "react-router-dom";
import update from "immutability-helper";
import * as XLSX from "xlsx";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const type = "DragableHeaderColumn";

const DragableHeaderCell = ({
  index,
  moveColumn,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();

  const [{ isOver }, drop] = useDrop({
    accept: type,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  let draggingStyle = {};
  if (isDragging) {
    draggingStyle = {
      background: "#e6f7ff",
      border: "2px dashed #1890ff",
      opacity: 0.7,
    };
  } else if (isOver) {
    draggingStyle = {
      background: "#f0f5ff",
    };
  }

  return (
    <th
      ref={ref}
      className={className}
      style={{ cursor: "move", userSelect: "none", ...style, ...draggingStyle }}
      {...restProps}
    />
  );
};

const HotelOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const token = localStorage.getItem("token");

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}hotelOrder`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      if (json.success && json.data?.data) {
        setOrders(json.data.data);
        setFilteredOrders(json.data.data);
      } else {
        message.error(json.message || "Failed to load hotel orders");
      }
    } catch (err) {
      console.error(err);
      message.error("Error fetching hotel orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const filtered = orders.filter((order) => {
      return (
        String(order.booking_Id).toLowerCase().includes(lower) ||
        (order.Hotel_name || "").toLowerCase().includes(lower) ||
        `${order.firstName || ""} ${order.lastName || ""}`
          .toLowerCase()
          .includes(lower)
      );
    });
    setFilteredOrders(filtered);
  }, [searchTerm, orders]);

  const initColumns = [
    {
      title: "Booking ID",
      dataIndex: "booking_Id",
      key: "booking_Id",
      render: (booking_Id) => (
        <Link to={`/hotel-orders/${booking_Id}`}>{booking_Id}</Link>
      ),
    },
    {
      title: "Hotel Name",
      dataIndex: "Hotel_name",
      key: "Hotel_name",
      sorter: (a, b) => (a.Hotel_name || "").localeCompare(b.Hotel_name || ""),
    },
    {
      title: "Booking Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) =>
        v ? dayjs.utc(v).local().format("YYYY-MM-DD HH:mm") : "-",
      sorter: (a, b) =>
        dayjs.utc(a.created_at).unix() - dayjs.utc(b.created_at).unix(),
    },
    {
      title: "Member ID",
      dataIndex: "member_id",
      key: "member_id",
      render: (member_id) => <Link to={`/user/${member_id}`}>{member_id}</Link>,
      sorter: (a, b) => a.member_id - b.member_id,
    },
    {
      title: "Supplier Booking ID",
      dataIndex: "supplier_Booking_Id",
      key: "supplier_Booking_Id",
    },
    {
      title: "Guest Name",
      key: "fullName",
      render: (_, record) =>
        `${record.firstName || ""} ${record.lastName || ""}`.trim(),
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
      render: (tag) => (
        <Tag color={tag === "RFN" ? "green" : "default"}>{tag}</Tag>
      ),
    },
    { title: "Check-in", dataIndex: "checkin", key: "checkin" },
    { title: "Check-out", dataIndex: "checkout", key: "checkout" },
    {
      title: "Commission",
      dataIndex: "client_commission",
      key: "client_commission",
      render: (c) => `$${parseFloat(c).toFixed(2)}`,
      sorter: (a, b) =>
        parseFloat(a.client_commission) - parseFloat(b.client_commission),
    },
    {
      title: "Price",
      dataIndex: "retail_rate",
      key: "retail_rate",
      render: (r) => `$${parseFloat(r).toFixed(2)}`,
      sorter: (a, b) => parseFloat(a.retail_rate) - parseFloat(b.retail_rate),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const color =
          s === "CONFIRMED" ? "green" : s === "CANCELLED" ? "red" : "gold";
        return <Tag color={color}>{s}</Tag>;
      },
      filters: [
        { text: "Confirmed", value: "CONFIRMED" },
        { text: "Cancelled", value: "CANCELLED" },
        { text: "Other", value: "OTHER" },
      ],
      onFilter: (val, rec) =>
        val === "OTHER"
          ? !["CONFIRMED", "CANCELLED"].includes(rec.status)
          : rec.status === val,
    },
  ];

  useEffect(() => {
    setColumns(initColumns);
  }, []);

  const moveColumn = (dragIndex, hoverIndex) => {
    const dragCol = columns[dragIndex];
    const newCols = update(columns, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragCol],
      ],
    });
    setColumns(newCols);
  };

  const components = {
    header: {
      cell: (props) => (
        <DragableHeaderCell {...props} moveColumn={moveColumn} />
      ),
    },
  };

  const finalColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: () => ({ index, moveColumn }),
  }));

  const handleExport = () => {
    const exportData = filteredOrders.map((row) => {
      const output = {};
      columns.forEach((col) => {
        let cellValue;
        if (col.render) {
          const rendered = col.render(row[col.dataIndex], row);
          if (typeof rendered === "string" || typeof rendered === "number") {
            cellValue = rendered;
          } else if (React.isValidElement(rendered)) {
            cellValue = rendered.props.children ?? "";
          } else {
            cellValue = "";
          }
        } else {
          cellValue = row[col.dataIndex] ?? "";
        }
        output[col.title] = cellValue;
      });
      return output;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Hotel Orders");
    XLSX.writeFile(workbook, "hotel_orders.xlsx");
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ padding: 24 }}>
        <h2>Hotel Orders</h2>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Input
            placeholder="Search by Booking ID, Guest Name, or Hotel Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: 16, maxWidth: 460 }}
            allowClear
          />
          <Button
            type="primary"
            onClick={handleExport}
            icon={<ExportOutlined />}
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 128, 0, 0.1)",
              color: "#008000",
              border: "1px solid #008000",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.2)")
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.1)")
            }
          >
            Export to Excel
          </Button>
        </div>

        {loading ? (
          <Spin size="large" />
        ) : (
          <>
            <div
              style={{
                margin: "16px 0",
                padding: "8px 16px",
                backgroundColor: "#fafafa",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: "14px",
                color: "#555",
              }}
            >
              Showing{" "}
              <span style={{ color: "#1890ff", fontWeight: 600 }}>
                {filteredOrders.length}
              </span>{" "}
              result{filteredOrders.length !== 1 ? "s" : ""} found
            </div>
            <div className="compact-table-wrapper">
              <Table
                dataSource={filteredOrders}
                columns={finalColumns}
                components={components}
                rowKey="id"
                bordered
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                }}
                scroll={{ x: "max-content" }}
              />
            </div>
          </>
        )}
      </div>
    </DndProvider>
  );
};

export default HotelOrdersPage;
